import { pricingPlansModalOpenAtom } from '../../billingAtoms';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';
import { trackPricingPlansModalCta } from './track-pricing-plans-modal-cta';

import { isComplimentaryCreditAllowance } from '@heidi/commerce/data';
import {
  Banner,
  BannerAction,
  BannerContent,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, type ReactNode } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import { trialExtensionModalOpenAtom } from '../../../../../data/atoms/freeTrialModalAtoms';
import { useReverseTrial } from '../../../../../data/injected-billing';
import { formatCreditAllowanceCycleEndDate } from '../../../../trials-and-upgrades/credit-usage-popover/format-credit-allowance-reset-label';
import { isHeidi2OptInCreditUsageEnabled } from '../../../is-credit-usage-surface-enabled';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';

function trialPlanName(
  newProductTier: SubscriptionTier | null | undefined,
  legacyTier: SubscriptionTier | null | undefined
): MessageDescriptor {
  switch (newProductTier) {
    case SubscriptionTier.CLINICIAN_PRO_PLUS:
      return pricingPlansModalMessages.planNameClinicianProPlus;
    case SubscriptionTier.EVIDENCE:
      return pricingPlansModalMessages.planNameEvidence;
    case SubscriptionTier.CLINICIAN_PRO:
      return pricingPlansModalMessages.planNameClinicianPro;
    default:
      return legacyTier === SubscriptionTier.PRO
        ? pricingPlansModalMessages.planNamePro
        : pricingPlansModalMessages.planNameClinicianPro;
  }
}

export function PricingPlansStatusBanner() {
  const intl = useIntl();
  const isOpen = useAtomValue(pricingPlansModalOpenAtom);
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const setTrialExtensionModalOpen = useSetAtom(trialExtensionModalOpenAtom);
  const isHeidi2OptInEnabled = getCommerceBillingUiPorts().useIsHeidi2OptInEnabled();
  const isTrialExtensionEntryEnabled =
    getCommerceBillingUiPorts().useIsGamifyTrialExtensionEntryEnabled();
  const { data: allowance } = getCommerceBillingUiPorts().useQueryCreditAllowance(isOpen);
  const {
    isInReverseTrial,
    isFakeClinicianTrial,
    isClinicianTrialUser,
    isClinicianPracticeTrialUser,
    isEvidenceTrialUser,
    isEvidenceInFreeTrial,
    clinicianTrialDaysLeft,
    clinicianTrialHoursLeft,
    trialDaysLeft,
    trialHoursLeft,
    evidenceTrialDaysLeft,
    heidiVersion,
    newProductTier,
    reverseTrialTier,
    seatType,
  } = useReverseTrial();

  const isTrialing = isInReverseTrial || isFakeClinicianTrial || isEvidenceInFreeTrial;
  const isComplimentaryUsage =
    isHeidi2OptInCreditUsageEnabled({
      isHeidi2OptInEnabled,
      heidiVersion,
    }) && isComplimentaryCreditAllowance(allowance);
  const showExtendTrial =
    isTrialing &&
    !getCommerceBillingUiPorts().isAppleCompliantBuild() &&
    isTrialExtensionEntryEnabled;

  const handleExtendTrial = useCallback(() => {
    trackPricingPlansModalCta({
      action: ButtonAction.PricingPlansModalExtendTrial,
      label: intl.formatMessage(pricingPlansModalMessages.extendTrial),
      currentPlan: newProductTier ?? undefined,
      currentSeatType: seatType,
      isInReverseTrial,
      isFakeClinicianTrial,
    });
    setPricingPlansModalOpen(false);
    setTrialExtensionModalOpen(true);
  }, [
    newProductTier,
    seatType,
    intl,
    isFakeClinicianTrial,
    isInReverseTrial,
    setPricingPlansModalOpen,
    setTrialExtensionModalOpen,
  ]);

  if (!isOpen || (!isTrialing && !isComplimentaryUsage)) return null;

  const onClinicianClock =
    isClinicianTrialUser ||
    isClinicianPracticeTrialUser ||
    isFakeClinicianTrial ||
    newProductTier === SubscriptionTier.CLINICIAN_PRO ||
    newProductTier === SubscriptionTier.CLINICIAN_PRO_PLUS;
  const remaining = !isTrialing
    ? null
    : onClinicianClock
      ? clinicianTrialHoursLeft < 24
        ? { unit: 'hours' as const, value: clinicianTrialHoursLeft }
        : { unit: 'days' as const, value: clinicianTrialDaysLeft }
      : isEvidenceTrialUser || isEvidenceInFreeTrial
        ? { unit: 'days' as const, value: evidenceTrialDaysLeft }
        : trialHoursLeft > 0 && trialHoursLeft < 24
          ? { unit: 'hours' as const, value: trialHoursLeft }
          : { unit: 'days' as const, value: trialDaysLeft };

  const learnMoreHref = getCommerceBillingUiPorts().billingUrls.getUsageLimitsHelpUrl(
    intl.locale
  );
  const learnMore = (chunks: ReactNode) => (
    <Link
      variant="inline"
      size="inherit"
      href={learnMoreHref}
      target="_blank"
      rel="noopener noreferrer"
      className="text-tier-pro-text decoration-tier-pro-text/50 hover:text-tier-pro-text hover:decoration-tier-pro-text"
    >
      {chunks}
    </Link>
  );

  return (
    <Banner subVariant="pro" data-testid="pricing-plans-status-banner">
      <BannerContent>
        <BannerTitle>
          {remaining != null ? (
            <FormattedMessage
              {...(remaining.unit === 'hours'
                ? pricingPlansModalMessages.trialHoursLeftBanner
                : pricingPlansModalMessages.trialDaysLeftBanner)}
              values={{
                count: remaining.value,
                plan: intl.formatMessage(trialPlanName(newProductTier, reverseTrialTier)),
                learnMore,
              }}
            />
          ) : (
            <FormattedMessage
              {...pricingPlansModalMessages.complimentaryUsageBanner}
              values={{
                date: formatCreditAllowanceCycleEndDate(intl, allowance?.cycle_end ?? ''),
                learnMore,
              }}
            />
          )}
        </BannerTitle>
      </BannerContent>
      {showExtendTrial ? (
        <BannerAction>
          <Button
            type="button"
            variant="surface"
            size="xs"
            onClick={handleExtendTrial}
            data-testid="pricing-plans-status-banner-extend-trial"
          >
            <FormattedMessage {...pricingPlansModalMessages.extendTrial} />
          </Button>
        </BannerAction>
      ) : null}
    </Banner>
  );
}
